/*

The MIT License (MIT)

Copyright (c) 2015-2017 Cheton Wu

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.

*/

// parseLine was taken out of gcode-parser as it's the only piece we were using and it has dependencies on Node's fs module
// which was polyfilled, but is no longer. It was easier to simply copy this function over than to worry about
// the fs module polyfill. The module looks fairly stale, but if its ever updated to work seamlessly, this should be
// fairly easy to update. We've may need to implement our own parser anyway, if we want better support of our actual G code
// implementation in Kinetic Control.

// Latest source: https://github.com/cncjs/gcode-parser/blob/master/src/index.js
// Copied from this commit: https://github.com/cncjs/gcode-parser/blob/9b4155ded6e61d01bc641e0c9cd7f3ead91258aa/src/index.js

// @param {string} line The G-code line
export const parseLine = (() => {
  // http://reprap.org/wiki/G-code#Special_fields
  // The checksum "cs" for a GCode string "cmd" (including its line number) is computed
  // by exor-ing the bytes in the string up to and not including the * character.
  const computeChecksum = (s) => {
    s = s || '';
    if (s.lastIndexOf('*') >= 0) {
      s = s.substr(0, s.lastIndexOf('*'));
    }

    let cs = 0;
    for (let i = 0; i < s.length; ++i) {
      const c = s[i].charCodeAt(0);
      cs ^= c;
    }
    return cs;
  };
  // http://linuxcnc.org/docs/html/gcode/overview.html#gcode:comments
  // Comments can be embedded in a line using parentheses () or for the remainder of a lineusing a semi-colon. The semi-colon is not treated as the start of a comment when enclosed in parentheses.
  const stripComments = (() => {
    const re1 = new RegExp(/\s*\([^)]*\)/g); // Remove anything inside the parentheses
    const re2 = new RegExp(/\s*;.*/g); // Remove anything after a semi-colon to the end of the line, including preceding spaces
    const re3 = new RegExp(/\s+/g);
    return (line => line.replace(re1, '').replace(re2, '').replace(re3, ''));
  })();
  const re = /(%.*)|({.*)|((?:\$\$)|(?:\$[a-zA-Z0-9#]*))|([a-zA-Z][0-9+\-.]+)|(\*[0-9]+)/igm;

  return (line, options) => {
    options = options || {};
    options.flatten = !!options.flatten;
    options.noParseLine = !!options.noParseLine;

    const result = {
      line: line
    };

    if (options.noParseLine) {
      return result;
    }

    result.words = [];

    let ln; // Line number
    let cs; // Checksum
    const words = stripComments(line).match(re) || [];

    for (let i = 0; i < words.length; ++i) {
      const word = words[i];
      const letter = word[0].toUpperCase();
      const argument = word.slice(1);

      // Parse % commands for bCNC and CNCjs
      // - %wait Wait until the planner queue is empty
      if (letter === '%') {
        result.cmds = (result.cmds || []).concat(line.trim());
        continue;
      }

      // Parse JSON commands for TinyG and g2core
      if (letter === '{') {
        result.cmds = (result.cmds || []).concat(line.trim());
        continue;
      }

      // Parse $ commands for Grbl
      // - $C Check gcode mode
      // - $H Run homing cycle
      if (letter === '$') {
        result.cmds = (result.cmds || []).concat(`${letter}${argument}`);
        continue;
      }

      // N: Line number
      if (letter === 'N' && typeof ln === 'undefined') {
        // Line (block) number in program
        ln = Number(argument);
        continue;
      }

      // *: Checksum
      if (letter === '*' && typeof cs === 'undefined') {
        cs = Number(argument);
        continue;
      }

      let value = Number(argument);
      if (Number.isNaN(value)) {
        value = argument;
      }

      if (options.flatten) {
        result.words.push(letter + value);
      } else {
        result.words.push([letter, value]);
      }
    }

    // Line number
    (typeof (ln) !== 'undefined') && (result.ln = ln);

    // Checksum
    (typeof (cs) !== 'undefined') && (result.cs = cs);
    if (result.cs && (computeChecksum(line) !== result.cs)) {
      result.err = true; // checksum failed
    }

    return result;
  };
})();
